import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import scheduledTaskService from '../../services/scheduledTaskService'

// Async thunks
export const fetchScheduledTasks = createAsyncThunk(
  'scheduledTask/fetchScheduledTasks',
  async (params, { rejectWithValue }) => {
    try {
      const response = await scheduledTaskService.getScheduledTasks(params)
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const cancelScheduledTask = createAsyncThunk(
  'scheduledTask/cancelScheduledTask',
  async (taskId, { rejectWithValue }) => {
    try {
      const response = await scheduledTaskService.cancelScheduledTask(taskId)
      return { taskId, ...response }
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const createScheduledTask = createAsyncThunk(
  'scheduledTask/createScheduledTask',
  async (taskData, { rejectWithValue }) => {
    try {
      const response = await scheduledTaskService.createScheduledTask(taskData)
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

export const fetchScheduledTaskStats = createAsyncThunk(
  'scheduledTask/fetchScheduledTaskStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await scheduledTaskService.getStats()
      return response
    } catch (error) {
      return rejectWithValue(error.message)
    }
  }
)

const scheduledTaskSlice = createSlice({
  name: 'scheduledTask',
  initialState: {
    tasks: [],
    stats: {
      total: 0,
      active: 0,
      paused: 0,
      pendingPayment: 0,
      paymentFailed: 0,
      canceled: 0,
    },
    loading: false,
    actionLoading: false,
    error: null,
    pagination: {
      currentPage: 0,
      pageSize: 12,
      totalElements: 0,
      totalPages: 0,
    },
  },
  reducers: {
    resetError: (state) => {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Fetch scheduled tasks
    builder
      .addCase(fetchScheduledTasks.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchScheduledTasks.fulfilled, (state, action) => {
        state.loading = false
        state.tasks = action.payload.content || action.payload.tasks || []
        state.pagination = {
          currentPage: action.payload.currentPage || 0,
          pageSize: action.payload.pageSize || 12,
          totalElements: action.payload.totalElements || 0,
          totalPages: action.payload.totalPages || 0,
        }
      })
      .addCase(fetchScheduledTasks.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })

    // Cancel scheduled task
    builder
      .addCase(cancelScheduledTask.pending, (state) => {
        state.actionLoading = true
      })
      .addCase(cancelScheduledTask.fulfilled, (state, action) => {
        state.actionLoading = false
        const index = state.tasks.findIndex((t) => t.id === action.payload.taskId)
        if (index !== -1) {
          state.tasks[index] = { ...state.tasks[index], subscriptionStatus: 'CANCELED' }
        }
      })
      .addCase(cancelScheduledTask.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Create scheduled task
    builder
      .addCase(createScheduledTask.pending, (state) => {
        state.actionLoading = true
      })
      .addCase(createScheduledTask.fulfilled, (state) => {
        state.actionLoading = false
      })
      .addCase(createScheduledTask.rejected, (state, action) => {
        state.actionLoading = false
        state.error = action.payload
      })

    // Fetch stats
    builder
      .addCase(fetchScheduledTaskStats.fulfilled, (state, action) => {
        state.stats = action.payload
      })
  },
})

export const { resetError } = scheduledTaskSlice.actions
export default scheduledTaskSlice.reducer
